import JSZip from 'jszip';
import { ydocToJSON } from '@narrativebench/editor-core';
import type { ExportLayoutOptions, ExportOptions, EpubChapter, EpubTocEntry, ChapterDraft } from './export-utils';
import {
  getEpubBodyFontFamilyCss,
  getExportBodyFontSizePt,
  getExportFilename,
  resolveExportDocumentJson,
} from './export-utils';

const escapeXHTML = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const generateAnchorId = (text: string, index: number): string => {
  const slug = text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 50);
  return `${slug}-${index}`;
};

const convertNodeToXHTML = (node: any, headingCounter: { count: number }, headings: Array<{ id: string; title: string; level: number }>): string => {
  if (node.type === 'paragraph') {
    const textContent = node.content?.map((child: any) => {
      if (child.type === 'text') {
        let text = escapeXHTML(child.text || '');
        if (child.marks) {
          child.marks.forEach((mark: any) => {
            if (mark.type === 'bold') text = `<strong>${text}</strong>`;
            if (mark.type === 'italic') text = `<em>${text}</em>`;
            if (mark.type === 'underline') text = `<u>${text}</u>`;
            if (mark.type === 'strike') text = `<s>${text}</s>`;
          });
        }
        return text;
      } else if (child.type === 'hardBreak') {
        return '<br/>';
      }
      return '';
    }).join('') || '';
    return textContent ? `<p>${textContent}</p>\n` : '';
  } else if (node.type === 'heading') {
    const level = node.attrs?.level || 1;
    const textContent = node.content?.map((child: any) => {
      if (child.type === 'text') return escapeXHTML(child.text || '');
      return '';
    }).join('') || '';
    if (!textContent.trim()) return '';
    const anchorId = generateAnchorId(textContent, headingCounter.count++);
    headings.push({ id: anchorId, title: textContent, level });
    return `<h${level} id="${anchorId}">${textContent}</h${level}>\n`;
  } else if (node.type === 'bulletList') {
    const items = node.content?.map((listItem: any) => {
      if (listItem.type === 'listItem' && listItem.content) {
        const itemContent = listItem.content.map((item: any) => convertNodeToXHTML(item, headingCounter, headings)).join('');
        return `<li>${itemContent}</li>`;
      }
      return '';
    }).filter((s: string) => s).join('\n') || '';
    return items ? `<ul>\n${items}\n</ul>\n` : '';
  } else if (node.type === 'orderedList') {
    const items = node.content?.map((listItem: any) => {
      if (listItem.type === 'listItem' && listItem.content) {
        const itemContent = listItem.content.map((item: any) => convertNodeToXHTML(item, headingCounter, headings)).join('');
        return `<li>${itemContent}</li>`;
      }
      return '';
    }).filter((s: string) => s).join('\n') || '';
    return items ? `<ol>\n${items}\n</ol>\n` : '';
  } else if (node.type === 'blockquote') {
    const content = node.content?.map((child: any) => convertNodeToXHTML(child, headingCounter, headings)).join('') || '';
    return content ? `<blockquote>\n${content}</blockquote>\n` : '';
  } else if (node.type === 'horizontalRule') {
    return '<hr/>\n';
  } else if (node.type === 'codeBlock') {
    const textContent = node.content?.map((child: any) => {
      if (child.type === 'text') return escapeXHTML(child.text || '');
      return '';
    }).join('') || '';
    return `<pre><code>${textContent}</code></pre>\n`;
  }
  return '';
};

const splitIntoChapters = (json: any, bookTitle: string): EpubChapter[] => {
  const chapters: EpubChapter[] = [];
  const nodes: any[] = json.content || [];

  let currentChapter: ChapterDraft | null = null;
  let chapterIndex = 0;
  const prologueNodes: any[] = [];

  const finalizeChapter = (draft: ChapterDraft, index: number): EpubChapter => {
    const headingCounter = { count: 0 };
    const headings: Array<{ id: string; title: string; level: number }> = [];
    const content = draft.nodes.map((n: any) => convertNodeToXHTML(n, headingCounter, headings)).join('');
    return {
      id: `chapter-${index}`,
      title: draft.title,
      filename: `chapter-${index}.xhtml`,
      content,
      headings,
    };
  };

  for (const node of nodes) {
    if (node.type === 'heading' && node.attrs?.level === 1) {
      if (currentChapter) {
        chapters.push(finalizeChapter(currentChapter, chapterIndex));
        chapterIndex++;
      } else if (prologueNodes.length > 0) {
        const headingCounter = { count: 0 };
        const headings: Array<{ id: string; title: string; level: number }> = [];
        const content = prologueNodes.map((n: any) => convertNodeToXHTML(n, headingCounter, headings)).join('');
        chapters.push({
          id: 'prologue',
          title: bookTitle,
          filename: 'prologue.xhtml',
          content,
          headings,
        });
      }

      const titleText = node.content?.map((child: any) => child.text || '').join('') || `Chapter ${chapterIndex + 1}`;
      currentChapter = { title: titleText, nodes: [node], headings: [] };
    } else if (currentChapter) {
      currentChapter.nodes.push(node);
    } else {
      prologueNodes.push(node);
    }
  }

  if (currentChapter) {
    chapters.push(finalizeChapter(currentChapter, chapterIndex));
  } else if (prologueNodes.length > 0) {
    const headingCounter = { count: 0 };
    const headings: Array<{ id: string; title: string; level: number }> = [];
    const content = prologueNodes.map((n: any) => convertNodeToXHTML(n, headingCounter, headings)).join('');
    chapters.push({
      id: 'content',
      title: bookTitle,
      filename: 'content.xhtml',
      content,
      headings,
    });
  }

  return chapters;
};

const buildTocTree = (chapters: EpubChapter[]): EpubTocEntry[] => {
  const tocEntries: EpubTocEntry[] = [];

  chapters.forEach((chapter) => {
    const chapterEntry: EpubTocEntry = {
      id: chapter.id,
      title: chapter.title,
      filename: chapter.filename,
      level: 1,
      children: [],
    };

    let currentH2: EpubTocEntry | null = null;

    chapter.headings.forEach((heading) => {
      if (heading.level === 2) {
        currentH2 = {
          id: heading.id,
          title: heading.title,
          filename: chapter.filename,
          anchor: heading.id,
          level: 2,
          children: [],
        };
        chapterEntry.children.push(currentH2);
      } else if (heading.level === 3 && currentH2) {
        currentH2.children.push({
          id: heading.id,
          title: heading.title,
          filename: chapter.filename,
          anchor: heading.id,
          level: 3,
          children: [],
        });
      } else if (heading.level === 3) {
        chapterEntry.children.push({
          id: heading.id,
          title: heading.title,
          filename: chapter.filename,
          anchor: heading.id,
          level: 3,
          children: [],
        });
      }
    });

    tocEntries.push(chapterEntry);
  });

  return tocEntries;
};

const renderTocEntries = (entries: EpubTocEntry[], indent: number = 0): string => {
  if (entries.length === 0) return '';

  const items = entries.map((entry) => {
    const href = entry.anchor ? `${entry.filename}#${entry.anchor}` : entry.filename;
    const childrenHtml = entry.children.length > 0 ? `\n<ol>\n${renderTocEntries(entry.children, indent + 1)}</ol>` : '';
    return `<li><a href="${href}">${escapeXHTML(entry.title)}</a>${childrenHtml}</li>`;
  }).join('\n');

  return items;
};

const renderNcxNavPoints = (entries: EpubTocEntry[], playOrder: { count: number }): string => {
  return entries.map((entry) => {
    const href = entry.anchor ? `${entry.filename}#${entry.anchor}` : entry.filename;
    const currentOrder = playOrder.count++;
    const childrenXml = entry.children.length > 0 ? renderNcxNavPoints(entry.children, playOrder) : '';
    return `<navPoint id="navpoint-${currentOrder}" playOrder="${currentOrder}">
  <navLabel><text>${escapeXHTML(entry.title)}</text></navLabel>
  <content src="${href}"/>
  ${childrenXml}
</navPoint>`;
  }).join('\n');
};

export const exportToEpub = async (options: ExportOptions): Promise<void> => {
  const { editor, ydoc, documentTitle, projectTitle, documentId, token, apiBase, t } = options;

  if (!editor) return;

  try {
    const defaultExportLayoutOptions: ExportLayoutOptions = {
      pageNumberPosition: 'bottom_right',
      lineSpacing: 1.5,
      marginsCm: {
        top: 2.5,
        right: 2.5,
        bottom: 2.5,
        left: 2.5,
      },
    };
    const exportLayoutOptions = options.exportLayoutOptions ?? defaultExportLayoutOptions;
    const epubBodyFontFamily = getEpubBodyFontFamilyCss(exportLayoutOptions.exportFontId);
    const epubBodyFontSizePt = getExportBodyFontSizePt(exportLayoutOptions.exportFontSizePt);

    const json = resolveExportDocumentJson(editor, ydoc, ydocToJSON);
    if (!json) return;
    const title = documentTitle || 'Proyecto';
    const titleEscaped = escapeXHTML(title);
    const bookId = `urn:uuid:${documentId || crypto.randomUUID()}`;
    const modifiedDate = new Date().toISOString().split('.')[0] + 'Z';

    const chapters = splitIntoChapters(json, title);
    const tocTree = buildTocTree(chapters);

    const zip = new JSZip();

    zip.file('mimetype', 'application/epub+zip', { compression: 'STORE' });

    const containerXml = `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/>
  </rootfiles>
</container>`;
    zip.file('META-INF/container.xml', containerXml);

    const stylesCss = `@charset "UTF-8";

body {
  font-family: ${epubBodyFontFamily};
  font-size: ${epubBodyFontSizePt}pt;
  line-height: 1.7;
  margin: 1.5em;
  text-align: justify;
  hyphens: auto;
  -webkit-hyphens: auto;
}

h1 {
  font-size: 2em;
  font-weight: bold;
  margin: 2em 0 1em 0;
  text-align: center;
  page-break-before: always;
  page-break-after: avoid;
}

h1:first-of-type {
  page-break-before: avoid;
}

h2 {
  font-size: 1.5em;
  font-weight: bold;
  margin: 1.5em 0 0.75em 0;
  page-break-after: avoid;
}

h3 {
  font-size: 1.25em;
  font-weight: bold;
  margin: 1.25em 0 0.5em 0;
  page-break-after: avoid;
}

h4, h5, h6 {
  font-size: 1.1em;
  font-weight: bold;
  margin: 1em 0 0.5em 0;
}

p {
  margin: 0 0 0.75em 0;
  text-indent: 1.5em;
}

p:first-of-type,
h1 + p, h2 + p, h3 + p, h4 + p, h5 + p, h6 + p,
blockquote + p, hr + p {
  text-indent: 0;
}

blockquote {
  margin: 1em 2em;
  font-style: italic;
  border-left: 3px solid #666;
  padding-left: 1em;
}

ul, ol {
  margin: 1em 0;
  padding-left: 2em;
}

li {
  margin: 0.5em 0;
}

hr {
  border: none;
  border-top: 1px solid #ccc;
  margin: 2em auto;
  width: 50%;
}

pre {
  font-family: "Courier New", monospace;
  font-size: 0.9em;
  background: #f5f5f5;
  padding: 1em;
  overflow-x: auto;
  white-space: pre-wrap;
}

code {
  font-family: "Courier New", monospace;
  font-size: 0.9em;
}

strong, b { font-weight: bold; }
em, i { font-style: italic; }
u { text-decoration: underline; }
s, strike { text-decoration: line-through; }

nav#toc ol {
  list-style-type: none;
  padding-left: 0;
}

nav#toc li {
  margin: 0.5em 0;
}

nav#toc ol ol {
  padding-left: 1.5em;
  margin: 0.25em 0;
}

nav#toc a {
  text-decoration: none;
  color: inherit;
}

.title-page {
  text-align: center;
  margin-top: 30%;
}

.title-page h1 {
  font-size: 2.5em;
  margin-bottom: 1em;
  page-break-before: avoid;
}`;
    zip.file('OEBPS/styles.css', stylesCss);

    const titlePageXhtml = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xml:lang="es" lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>${titleEscaped}</title>
  <link rel="stylesheet" type="text/css" href="styles.css"/>
</head>
<body>
  <section class="title-page">
    <h1>${titleEscaped}</h1>
  </section>
</body>
</html>`;
    zip.file('OEBPS/title.xhtml', titlePageXhtml);

    chapters.forEach((chapter) => {
      const chapterXhtml = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xml:lang="es" lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>${escapeXHTML(chapter.title)}</title>
  <link rel="stylesheet" type="text/css" href="styles.css"/>
</head>
<body>
${chapter.content}
</body>
</html>`;
      zip.file(`OEBPS/${chapter.filename}`, chapterXhtml);
    });

    const tocHtml = renderTocEntries(tocTree);
    const navXhtml = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops" xml:lang="es" lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>${t('table_of_contents')}</title>
  <link rel="stylesheet" type="text/css" href="styles.css"/>
</head>
<body>
  <nav epub:type="toc" id="toc">
    <h1>${t('table_of_contents')}</h1>
    <ol>
${tocHtml}
    </ol>
  </nav>
</body>
</html>`;
    zip.file('OEBPS/nav.xhtml', navXhtml);

    const playOrder = { count: 1 };
    const ncxNavPoints = renderNcxNavPoints(tocTree, playOrder);
    const tocNcx = `<?xml version="1.0" encoding="UTF-8"?>
<ncx xmlns="http://www.daisy.org/z3986/2005/ncx/" version="2005-1">
  <head>
    <meta name="dtb:uid" content="${bookId}"/>
    <meta name="dtb:depth" content="3"/>
    <meta name="dtb:totalPageCount" content="0"/>
    <meta name="dtb:maxPageNumber" content="0"/>
  </head>
  <docTitle>
    <text>${titleEscaped}</text>
  </docTitle>
  <navMap>
${ncxNavPoints}
  </navMap>
</ncx>`;
    zip.file('OEBPS/toc.ncx', tocNcx);

    const manifestItems = [
      '<item id="nav" href="nav.xhtml" media-type="application/xhtml+xml" properties="nav"/>',
      '<item id="ncx" href="toc.ncx" media-type="application/x-dtbncx+xml"/>',
      '<item id="styles" href="styles.css" media-type="text/css"/>',
      '<item id="title" href="title.xhtml" media-type="application/xhtml+xml"/>',
      ...chapters.map((ch) => `<item id="${ch.id}" href="${ch.filename}" media-type="application/xhtml+xml"/>`),
    ].join('\n    ');

    const spineItems = [
      '<itemref idref="title"/>',
      '<itemref idref="nav"/>',
      ...chapters.map((ch) => `<itemref idref="${ch.id}"/>`),
    ].join('\n    ');

    const contentOpf = `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" version="3.0" unique-identifier="book-id" xml:lang="es">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/">
    <dc:identifier id="book-id">${bookId}</dc:identifier>
    <dc:title>${titleEscaped}</dc:title>
    <dc:language>es</dc:language>
    <dc:creator>Scribane</dc:creator>
    <meta property="dcterms:modified">${modifiedDate}</meta>
  </metadata>
  <manifest>
    ${manifestItems}
  </manifest>
  <spine toc="ncx">
    ${spineItems}
  </spine>
</package>`;
    zip.file('OEBPS/content.opf', contentOpf);

    const blob = await zip.generateAsync({
      type: 'blob',
      mimeType: 'application/epub+zip',
      compression: 'DEFLATE',
      compressionOptions: { level: 9 },
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = getExportFilename(documentTitle, projectTitle, 'epub');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    if (token) {
      try {
        await fetch(`${apiBase}/api/exports`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            documentId: documentId || null,
            exportType: 'epub',
            documentTitle: documentTitle || null,
          }),
        });
      } catch (err) {
        console.error('Error tracking export:', err);
      }
    }
  } catch (err) {
    console.error('Error exporting to EPUB:', err);
    alert('Error al exportar el proyecto. Por favor, inténtalo de nuevo.');
  }
};

